import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ErrorBoundary from './components/common/ErrorBoundary';
import Home from './pages/Home';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import Automation from './pages/Automation';
import AutomationDetail from './pages/AutomationDetail';
import QA from './pages/qa/QA';
import NewQuestion from './pages/qa/NewQuestion';
import QuestionDetail from './pages/qa/QuestionDetail';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import AdminEditorLogin from './pages/auth/AdminEditorLogin';
import DashboardHome from './pages/admin/DashboardHome';
import BlogManagement from './pages/admin/BlogManagement';
import BlogForm from './pages/admin/blog/BlogForm';
import AutomationManagement from './pages/admin/AutomationManagement';
import AutomationForm from './pages/admin/automation/AutomationForm';
import UserManagement from './pages/admin/UserManagement';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import CookiePolicy from './pages/legal/CookiePolicy';
import LegalNotice from './pages/legal/LegalNotice';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from './layouts/AdminLayout';
import EditorLayout from './layouts/EditorLayout';
import EditorDashboard from './pages/editor/EditorDashboard';
import EditorBlogList from './pages/editor/blog/BlogList';
import EditorBlogForm from './pages/editor/blog/BlogForm';
import EditorAutomationList from './pages/editor/automation/AutomationList';
import EditorAutomationForm from './pages/editor/automation/EditorAutomationForm';
import EditorRoute from './components/auth/EditorRoute';
import RequestForm from './pages/requests/RequestForm';
import UserRequests from './pages/requests/UserRequests';
import AdminRequests from './pages/admin/AdminRequests';
import { useNavigate } from 'react-router-dom';
import AdminRequestDetail from './pages/admin/AdminRequestDetail';
import UserRequestDetail from './pages/qa/UserRequestDetail';
import EditorMessages from './pages/editor/EditorMessages';
import EditorRequestDetail from './pages/editor/EditorRequestDetail';
import RequestManagement from './pages/admin/RequestManagement';
import setupAdminAndEditor from './config/setupAdmin';
import Notifications from './pages/Notifications';

const Layout = () => (
  <>
    <Navbar />
    <div id="content" className="min-h-screen">
      <Outlet />
    </div>
    <Footer />
  </>
);

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const ProtectedAdminRoute = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'editor')) {
      navigate('/');
    }
  }, [user, navigate]);

  return user && (user.role === 'admin' || user.role === 'editor') ? children : null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: "blog",
        element: <Blog />
      },
      {
        path: "blog/:id",
        element: <BlogDetail />
      },
      {
        path: "automation",
        element: <Automation />
      },
      {
        path: "automation/:id",
        element: <AutomationDetail />
      },
      {
        path: "qa",
        element: <QA />
      },
      {
        path: "qa/new",
        element: <PrivateRoute><NewQuestion /></PrivateRoute>
      },
      {
        path: "qa/:id",
        element: <PrivateRoute><QuestionDetail /></PrivateRoute>
      },
      {
        path: "login",
        element: <Login />
      },
      {
        path: "register",
        element: <Register />
      },
      {
        path: "privacy-policy",
        element: <Privacy />
      },
      {
        path: "terms-of-service",
        element: <Terms />
      },
      {
        path: "cookie-policy",
        element: <CookiePolicy />
      },
      {
        path: "legal-notice",
        element: <LegalNotice />
      },
      {
        path: "admin",
        children: [
          {
            path: "login",
            element: <AdminEditorLogin />
          },
          {
            element: <AdminRoute><AdminLayout /></AdminRoute>,
            children: [
              {
                index: true,
                element: <DashboardHome />
              },
              {
                path: "blogs",
                children: [
                  {
                    index: true,
                    element: <BlogManagement />
                  },
                  {
                    path: "new",
                    element: <BlogForm />
                  },
                  {
                    path: "edit/:id",
                    element: <BlogForm />
                  }
                ]
              },
              {
                path: "automations",
                children: [
                  {
                    index: true,
                    element: <AutomationManagement />
                  },
                  {
                    path: "new",
                    element: <AutomationForm />
                  },
                  {
                    path: "edit/:id",
                    element: <AutomationForm />
                  }
                ]
              },
              {
                path: "users",
                element: <UserManagement />
              },
              {
                path: "request-management",
                element: <RequestManagement />
              }
            ]
          }
        ]
      },
      {
        path: "editor/*",
        element: <EditorRoute><EditorLayout /></EditorRoute>,
        children: [
          {
            path: "*",
            element: <EditorDashboard />
          },
          {
            path: "blogs/*",
            children: [
              {
                path: "*",
                element: <EditorBlogList />
              },
              {
                path: "new",
                element: <EditorBlogForm />
              },
              {
                path: "edit/:id",
                element: <EditorBlogForm isEdit />
              }
            ]
          },
          {
            path: "automations/*",
            children: [
              {
                path: "*",
                element: <EditorAutomationList />
              },
              {
                path: "new",
                element: <EditorAutomationForm />
              },
              {
                path: "edit/:id",
                element: <EditorAutomationForm isEdit />
              }
            ]
          },
          {
            path: "messages/*",
            element: <EditorMessages />
          },
          {
            path: "requests/:requestId/*",
            element: <EditorRequestDetail />
          }
        ]
      },
      {
        path: "requests",
        element: <ProtectedRoute><UserRequests /></ProtectedRoute>
      },
      {
        path: "requests/new",
        element: <ProtectedRoute><RequestForm /></ProtectedRoute>
      },
      {
        path: "requests/:requestId",
        element: <ProtectedRoute><UserRequestDetail /></ProtectedRoute>
      },
      {
        path: "admin/requests",
        element: <ProtectedAdminRoute><AdminRequests /></ProtectedAdminRoute>
      },
      {
        path: "admin/requests/:requestId",
        element: <ProtectedAdminRoute><AdminRequestDetail /></ProtectedAdminRoute>
      },
      {
        path: "notifications",
        element: <Notifications />
      }
    ]
  }
]);

function App() {
  useEffect(() => {
    setupAdminAndEditor();
  }, []);
  
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}

export default App; 